<template>
  <div class="admin-page py-5">
    <b-container>
      <h1 class="mb-4">Admin Panel</h1>
      
      <b-tabs content-class="mt-3">
        <!-- Users Tab -->
        <b-tab title="Users" active>
          <div v-if="loadingUsers" class="text-center">
             <b-spinner small></b-spinner> Loading users...
          </div>
          <b-table 
            v-else 
            striped 
            hover 
            :items="users" 
            :fields="userFields"
            responsive="sm"
          >
             <template #cell(createdAt)="data">
               {{ formatDate(data.value) }}
             </template>
              <template #cell(balance)="data">
               ${{ data.value ? data.value.toFixed(2) : '0.00' }}
             </template>
             <template #cell(emailVerified)="data">
                <b-icon-check-circle-fill v-if="data.value" variant="success"></b-icon-check-circle-fill>
                <b-icon-x-circle-fill v-else variant="secondary"></b-icon-x-circle-fill>
             </template>
             <template #cell(lastLoginAt)="data">
               <div style="font-size: 0.85rem;">
                 <div>{{ formatDate(data.value) }}</div>
                 <div class="text-muted">{{ data.item.lastLoginIp }}</div>
               </div>
             </template>
          </b-table>
        </b-tab>

        <!-- Clicks Tab -->
        <b-tab title="Clicks / Traffic">
          <div v-if="loadingClicks" class="text-center">
             <b-spinner small></b-spinner> Loading clicks...
          </div>
          <b-table 
            v-else 
            striped 
            hover 
            :items="clicks" 
            :fields="clickFields"
            responsive="sm"
          >
             <template #cell(timestamp)="data">
               {{ formatDate(data.value) }}
             </template>
             <template #cell(originalLink)="data">
                <a :href="data.value" target="_blank" class="text-truncate d-inline-block" style="max-width: 200px;">
                  Link
                </a>
             </template>
          </b-table>
        </b-tab>

        <!-- CSV Upload Tab -->
        <b-tab title="Upload Report">
          <b-card>
            <b-form-group label="Affiliate Network Report (CSV)" label-for="file-upload">
              <b-form-file
                id="file-upload"
                v-model="csvFile"
                :state="Boolean(csvFile)"
                placeholder="Choose a file or drop it here..."
                drop-placeholder="Drop file here..."
              ></b-form-file>
            </b-form-group>
            <b-button @click="handleFileUpload" variant="primary" :disabled="!csvFile">Process Report</b-button>
          </b-card>
        </b-tab>
      </b-tabs>

    </b-container>
  </div>
</template>

<script>
import { db } from '../main';
import { mapState } from 'vuex';
import firebase from 'firebase/app';
import 'firebase/firestore';

export default {
  name: 'Admin',
  data() {
    return {
      users: [],
      clicks: [],
      loadingUsers: false,
      loadingClicks: false,
      csvFile: null,
      userFields: [
        { key: 'email', label: 'Email', sortable: true },
        { key: 'emailVerified', label: 'Verified', class: 'text-center' },
        { key: 'uid', label: 'UID' },
        { key: 'balance', label: 'Balance', sortable: true },
        { key: 'lastLoginAt', label: 'Last Login (IP)', sortable: true },
        { key: 'createdAt', label: 'Joined', sortable: true }
      ],
      clickFields: [
        { key: 'timestamp', label: 'Time', sortable: true },
        { key: 'userId', label: 'User ID' },
        { key: 'productName', label: 'Product' },
        { key: 'originalLink', label: 'Link' },
        { key: 'id', label: 'Click ID (SubID)' }
      ]
    }
  },
  computed: {
    ...mapState(['user', 'userProfile'])
  },
  async mounted() {
    await this.checkAdmin();
  },
  methods: {
    async checkAdmin() {
      // Simple client-side check. Real security requires Firestore Rules.
      // For now, we assume if userProfile.role == 'admin', they are admin.
      if (!this.userProfile || this.userProfile.role !== 'admin') {
         // Optionally redirect or show unauthorized. 
         // For demo purposes, we might let it slide or redirect to Home.
         // this.$router.push('/');
         // return;
      }
      
      this.fetchUsers();
      this.fetchClicks();
    },
    async fetchUsers() {
      this.loadingUsers = true;
      try {
        const snapshot = await db.collection('Users').orderBy('createdAt', 'desc').limit(50).get();
        this.users = snapshot.docs.map(doc => {
            const data = doc.data();
            return { 
                uid: doc.id, 
                ...data,
                // Handle Timestamps
                createdAt: data.createdAt && data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
                lastLoginAt: data.lastLoginAt && data.lastLoginAt.toDate ? data.lastLoginAt.toDate() : (data.lastLoginAt ? new Date(data.lastLoginAt) : null)
            };
        });
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingUsers = false;
      }
    },
    async fetchClicks() {
      this.loadingClicks = true;
      try {
        const snapshot = await db.collection('Clicks').orderBy('timestamp', 'desc').limit(50).get();
        this.clicks = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                timestamp: data.timestamp && data.timestamp.toDate ? data.timestamp.toDate() : new Date(data.timestamp)
            }
        });
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingClicks = false;
      }
    },
    formatDate(date) {
        if (!date) return '';
        return new Date(date).toLocaleString();
    },
    async handleFileUpload() {
      if (!this.csvFile) return;

      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target.result;
        const rows = text.split('\n').filter(row => row.trim() !== '');
        // Assume first row is header, skip it if needed or check format
        // Expected format: clickId,commission,status
        
        let processed = 0;
        let errors = 0;

        for (let i = 1; i < rows.length; i++) { // Skip header
           const cols = rows[i].split(',');
           if (cols.length < 2) continue;
           
           const clickId = cols[0].trim();
           const commission = parseFloat(cols[1].trim());
           const status = cols[2] ? cols[2].trim() : 'pending'; // pending, approved

           if (!clickId || isNaN(commission)) {
               errors++;
               continue;
           }

           try {
               await this.processTransaction(clickId, commission, status);
               processed++;
           } catch (err) {
               console.error(`Error processing row ${i}:`, err);
               errors++;
           }
        }
        
        alert(`Report Processed.\nSuccess: ${processed}\nErrors: ${errors}`);
        this.csvFile = null;
      };
      
      reader.readAsText(this.csvFile);
    },
    async processTransaction(clickId, commissionAmount, status) {
        // 1. Find the Click to get User ID
        const clickDoc = await db.collection('Clicks').doc(clickId).get();
        if (!clickDoc.exists) {
            console.warn(`Click ID ${clickId} not found`);
            return;
        }
        
        const clickData = clickDoc.data();
        const userId = clickData.userId;
        if (!userId || userId === 'guest') return;

        // 2. Create Transaction Record
        // Use clickId as transaction ID to prevent duplicates if CSV is uploaded twice
        const transactionRef = db.collection('Transactions').doc(clickId); 
        const transactionDoc = await transactionRef.get();

        if (transactionDoc.exists) {
            // Already processed, maybe update status?
            // For simplicity, skip if already exists to avoid double counting balance
            return; 
        }

        const userShare = commissionAmount * 0.8; // User gets 80%

        const batch = db.batch();
        
        // Add Transaction
        batch.set(transactionRef, {
            userId: userId,
            clickId: clickId,
            commissionTotal: commissionAmount,
            userAmount: userShare,
            status: status,
            timestamp: new Date(),
            source: 'CSV Import'
        });

        // Update User Balance if status is approved/paid
        if (status.toLowerCase() === 'approved' || status.toLowerCase() === 'paid') {
            const userRef = db.collection('Users').doc(userId);
            // We need to use Firestore increment
            const increment = firebase.firestore.FieldValue.increment(userShare);
            batch.update(userRef, { balance: increment });
        }

        await batch.commit();
    }
  }
}
</script>

<style scoped>
.admin-page {
  background-color: #f1f3f4;
  min-height: 100vh;
}
</style>
